// Expéditeur — what the recipient sees as the SMS author: Unanimity's sender
// (nothing to provide) or the company's own name, which OVH validates on
// documents (Kbis + legal representative's ID). The documents go straight to
// OVH; Unanimity keeps only their name and size.

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Building2, FileText, RefreshCw, Send, Trash2, Upload } from 'lucide-react';
import {
  Badge, Banner, Card, ConfirmDialog, DescriptionList, Field, Steps,
} from '../../design/ui';
import { del, fail, formatBytes, formatWhen, post, postForm } from './api';

const SENDER_RE = /^[A-Za-z0-9][A-Za-z0-9 .-]{1,9}[A-Za-z0-9]$/;
const DOCS = [
  { key: 'kbis', required: true },
  { key: 'id_front', required: true },
  { key: 'id_back', required: false },
];
const ACCEPT = '.pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png';

const PlatformStatus = ({ status, t }) => {
  if (status === 'enable') return <Badge tone="success" dot>{t('customerSms.sender.active', 'Actif')}</Badge>;
  if (!status) return <Badge>{t('customerSms.sender.checking', 'Vérification en cours')}</Badge>;
  return <Badge tone="warning" dot>{t('customerSms.sender.platformPending', 'Pas encore actif')}</Badge>;
};

const OWN_TONES = { pending: 'warning', active: 'success', refused: 'danger', disabled: 'danger' };

const OwnStatus = ({ own, t }) => {
  if (!own) return <Badge>{t('customerSms.sender.notRequested', 'Non demandé')}</Badge>;
  const tone = OWN_TONES[own.status];
  return <Badge tone={tone} dot={Boolean(tone)}>{t(`customerSms.sender.status.${own.status}`, own.status)}</Badge>;
};

const RequestForm = ({ defaults, limits, onDone, setFeedback, t }) => {
  const [name, setName] = useState(defaults.name || '');
  const [company, setCompany] = useState(defaults.company_name || '');
  const [siren, setSiren] = useState(defaults.siren || '');
  const [files, setFiles] = useState({});
  const [attest, setAttest] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const trimmed = name.trim().replace(/\s+/g, ' ');
  const nameOk = SENDER_RE.test(trimmed) && /[A-Za-z]/.test(trimmed);
  const sirenOk = /^\d{9}$|^\d{14}$/.test(siren.replace(/\s/g, ''));
  const tooBig = Object.values(files).some((f) => f && f.size > (limits?.document_max_bytes || 8 * 1024 * 1024));
  const ready = nameOk && company.trim().length > 1 && sirenOk && files.kbis && files.id_front && attest && !tooBig;

  const pick = (key) => (e) => {
    const file = e.target.files?.[0];
    setFiles((prev) => ({ ...prev, [key]: file || undefined }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!ready) return;
    const form = new FormData();
    form.append('name', trimmed);
    form.append('company_name', company.trim());
    form.append('siren', siren.replace(/\s/g, ''));
    form.append('attest', 'true');
    DOCS.forEach(({ key }) => { if (files[key]) form.append(key, files[key]); });
    setBusy(true);
    setError('');
    try {
      const res = await postForm('/sender', form);
      setFeedback({ tone: 'success', text: t('customerSms.sender.sent', 'Demande envoyée à OVH. Vous serez prévenu dès sa validation.') });
      onDone(res.data);
    } catch (err) {
      setError(fail(err, t('customerSms.sender.sendError', "La demande n'a pas pu être envoyée.")));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="u-form sms-v2__request" onSubmit={submit}>
      <div className="sms-v2__grid-3">
        <Field
          label={t('customerSms.sender.name', "Nom d'expéditeur")}
          htmlFor="sms-sender-name"
          error={name !== '' && !nameOk ? t('customerSms.sender.nameHint', '3 à 11 caractères : lettres, chiffres, espace, point ou tiret.') : null}
          help={`${t('customerSms.sender.nameHint', '3 à 11 caractères : lettres, chiffres, espace, point ou tiret.')} ${trimmed.length}/11`}
        >
          <input id="sms-sender-name" className="u-input" value={name} onChange={(e) => setName(e.target.value)} maxLength={11}
            placeholder="ATLAS" aria-invalid={name !== '' && !nameOk} />
        </Field>
        <Field label={t('customerSms.sender.company', 'Raison sociale')} htmlFor="sms-sender-company">
          <input id="sms-sender-company" className="u-input" value={company} onChange={(e) => setCompany(e.target.value)} maxLength={120}
            placeholder="Atlas Logistique SAS" />
        </Field>
        <Field label={t('customerSms.sender.siren', 'SIREN ou SIRET')} htmlFor="sms-sender-siren">
          <input id="sms-sender-siren" className="u-input" value={siren} onChange={(e) => setSiren(e.target.value)} maxLength={20}
            inputMode="numeric" placeholder="912 345 678" aria-invalid={siren !== '' && !sirenOk} />
        </Field>
      </div>

      <div className="u-field">
        <span className="u-label">{t('customerSms.sender.documents', 'Justificatifs')}</span>
        <div className="sms-v2__docs">
          {DOCS.map(({ key, required }) => (
            <label key={key} className={`sms-v2__doc${files[key] ? ' is-set' : ''}`}>
              <input type="file" accept={ACCEPT} onChange={pick(key)} />
              {files[key] ? <FileText strokeWidth={1.8} aria-hidden="true" /> : <Upload strokeWidth={1.8} aria-hidden="true" />}
              <span className="sms-v2__doc-text">
                <strong>{t(`customerSms.sender.docs.${key}`, key)}{required ? '' : ` ${t('customerSms.optional', '(facultatif)')}`}</strong>
                <small>
                  {files[key]
                    ? `${files[key].name} · ${formatBytes(files[key].size)}`
                    : t(`customerSms.sender.docsHint.${key}`, 'PDF, JPEG ou PNG · 8 Mo max.')}
                </small>
              </span>
            </label>
          ))}
        </div>
        {tooBig && <p className="u-error-text">{t('customerSms.sender.tooBig', 'Un fichier dépasse 8 Mo.')}</p>}
      </div>

      <label className="sms-v2__check">
        <input type="checkbox" checked={attest} onChange={(e) => setAttest(e.target.checked)} />
        <span>{t('customerSms.sender.attest', "J'atteste être habilité à représenter cette entreprise et à utiliser ce nom d'expéditeur.")}</span>
      </label>
      <p className="u-help">
        {t('customerSms.sender.privacy', 'Les documents sont transmis directement à OVH pour la validation et ne sont pas conservés par Unanimity.')}
      </p>
      {error && <Banner tone="danger">{error}</Banner>}
      <div className="sms-v2__actions">
        <button type="submit" className="u-btn u-btn--primary" disabled={!ready || busy}>
          <Send aria-hidden="true" />
          {busy ? t('customerSms.sender.sending', 'Envoi à OVH…') : t('customerSms.sender.submit', 'Envoyer la demande à OVH')}
        </button>
      </div>
    </form>
  );
};

const OwnProgress = ({ own, lang, canEdit, onChange, setFeedback, platformName, t }) => {
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const refresh = async () => {
    setBusy(true);
    try {
      const res = await post('/sender/refresh');
      onChange(res.data);
    } catch (err) {
      setFeedback({ tone: 'danger', text: fail(err, t('customerSms.sender.refreshError', 'OVH ne répond pas pour le moment.')) });
    } finally {
      setBusy(false);
    }
  };

  const withdraw = async () => {
    setBusy(true);
    try {
      const res = await del('/sender');
      setFeedback({ tone: 'success', text: t('customerSms.sender.withdrawn', 'Expéditeur retiré : vos SMS partent sous « Unanimity ».') });
      onChange(res.data);
    } catch (err) {
      setFeedback({ tone: 'danger', text: fail(err, t('customerSms.sender.withdrawError', "Le retrait n'a pas abouti.")) });
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  };

  const status = (done, current) => (done ? 'done' : current ? 'current' : 'todo');
  const steps = [
    {
      key: 'sent',
      status: 'done',
      title: t('customerSms.sender.stepSent', 'Demande envoyée'),
      description: own.submitted_at ? formatWhen(own.submitted_at, lang) : null,
    },
    {
      key: 'review',
      status: status(own.status === 'active', own.status === 'pending'),
      title: t('customerSms.sender.stepReview', 'Validation par OVH'),
      description: own.status === 'pending' ? t('customerSms.sender.reviewDelay', 'En général 2 à 5 jours ouvrés') : null,
    },
    {
      key: 'active',
      status: status(own.status === 'active', false),
      title: t('customerSms.sender.stepActive', 'Expéditeur actif'),
    },
  ];
  const isActive = own.status === 'active';

  return (
    <div className="sms-v2__progress">
      <Steps steps={steps} />
      <DescriptionList items={[
        { label: t('customerSms.sender.name', "Nom d'expéditeur"), value: <span className="u-code">{own.name}</span> },
        { label: t('customerSms.sender.company', 'Raison sociale'), value: `${own.company_name} · SIREN ${own.siren}` },
        {
          label: t('customerSms.sender.documents', 'Justificatifs'),
          value: (own.documents || []).map((d) => t(`customerSms.sender.docs.${d.kind}`, d.kind)).join(' · ') || '—',
        },
        own.checked_at && { label: t('customerSms.sender.checked', 'Dernière vérification'), value: formatWhen(own.checked_at, lang) },
      ]} />
      {canEdit && (
        <div className="sms-v2__actions">
          {own.status === 'pending' && (
            <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={refresh} disabled={busy}>
              <RefreshCw aria-hidden="true" />
              {t('customerSms.sender.refresh', 'Vérifier maintenant')}
            </button>
          )}
          <button type="button" className="u-btn u-btn--danger u-btn--sm" onClick={() => setConfirming(true)} disabled={busy}>
            <Trash2 aria-hidden="true" />
            {isActive ? t('customerSms.sender.remove', "Retirer l'expéditeur") : t('customerSms.sender.withdraw', 'Retirer la demande')}
          </button>
        </div>
      )}
      {confirming && (
        <ConfirmDialog
          title={t('channelsUi.sms.withdrawTitle', 'Retirer l’expéditeur « {{name}} » ?', { name: own.name })}
          message={isActive
            ? t('channelsUi.sms.withdrawActive', 'Il est supprimé chez OVH : vos SMS partiront de nouveau sous « {{platform}} ». Pour le retrouver, il faudra refaire une demande avec les justificatifs.', { platform: platformName })
            : t('channelsUi.sms.withdrawPending', 'La demande est retirée chez OVH : vos SMS continuent de partir sous « {{platform}} ».', { platform: platformName })}
          confirmLabel={t('customerSms.sender.withdrawYes', 'Oui, retirer')}
          cancelLabel={t('customerSms.cancel', 'Annuler')}
          icon={Trash2}
          busy={busy}
          onConfirm={withdraw}
          onCancel={() => setConfirming(false)}
        />
      )}
    </div>
  );
};

const SenderSection = ({ view, senderMode, onModeChange, onViewChange, setFeedback, lang }) => {
  const { t } = useTranslation();
  const own = view.sender.own;
  const platform = view.sender.platform;
  const canEdit = view.can_edit;
  const requestable = !own || ['refused', 'disabled'].includes(own.status);
  const [requesting, setRequesting] = useState(false);
  const ownSelected = senderMode === 'own' || (requesting && requestable);

  const choose = (mode) => {
    if (!canEdit) return;
    if (mode === 'own' && requestable) {
      setRequesting(true);
      return;
    }
    setRequesting(false);
    onModeChange(mode);
  };

  return (
    <Card
      id="sms-sender"
      icon={Building2}
      title={t('customerSms.sender.title', 'Expéditeur')}
      description={t('customerSms.sender.subtitle', 'Le nom affiché en haut du SMS chez vos destinataires.')}
    >
      <div className="u-form">
        <div className="sms-v2__options" role="radiogroup" aria-label={t('customerSms.sender.title', 'Expéditeur')}>
          <button type="button" role="radio" aria-checked={!ownSelected} disabled={!canEdit}
            className={`sms-v2__option${!ownSelected ? ' is-selected' : ''}`} onClick={() => choose('platform')}>
            <span className="sms-v2__option-head">
              <span className="sms-v2__radio" aria-hidden="true" />
              <strong>{platform.name}</strong>
              <PlatformStatus status={platform.status} t={t} />
            </span>
            <span className="sms-v2__option-body">
              {t('customerSms.sender.platformDesc', 'Vos SMS sont signés « {{name}} ». Rien à fournir, disponible tout de suite.', { name: platform.name })}
            </span>
          </button>
          <button type="button" role="radio" aria-checked={ownSelected} disabled={!canEdit}
            className={`sms-v2__option${ownSelected ? ' is-selected' : ''}`} onClick={() => choose('own')}>
            <span className="sms-v2__option-head">
              <span className="sms-v2__radio" aria-hidden="true" />
              <strong>{own?.name || t('customerSms.sender.ownTitle', 'Votre entreprise')}</strong>
              <OwnStatus own={own} t={t} />
            </span>
            <span className="sms-v2__option-body">
              {t('customerSms.sender.ownDesc', "Vos SMS portent le nom de votre entreprise. OVH le valide sur justificatifs : extrait Kbis de moins de 3 mois et pièce d'identité du représentant légal.")}
            </span>
          </button>
        </div>

        {ownSelected && own?.status === 'refused' && (
          <Banner tone="danger" title={t('customerSms.sender.refusedTitle', 'OVH a refusé la demande')}>
            {own.moderator_comment && <>{own.moderator_comment}. </>}
            {t('customerSms.sender.refusedHint', 'Corrigez le document concerné et envoyez une nouvelle demande.')}
          </Banner>
        )}
        {ownSelected && own?.status === 'disabled' && (
          <Banner tone="danger">
            {t('customerSms.sender.disabledHint', 'OVH a désactivé cet expéditeur. Envoyez une nouvelle demande ou revenez à « {{name}} ».', { name: platform.name })}
          </Banner>
        )}
        {ownSelected && own && ['pending', 'active'].includes(own.status) && (
          <>
            {own.status === 'pending' && (
              <Banner tone="info">
                {t('customerSms.sender.fallback', 'En attendant la validation, vos SMS partent sous « {{name}} ».', { name: platform.name })}
              </Banner>
            )}
            <OwnProgress own={own} lang={lang} canEdit={canEdit} onChange={onViewChange} setFeedback={setFeedback}
              platformName={platform.name} t={t} />
          </>
        )}
        {ownSelected && requestable && canEdit && (
          <div className="sms-v2__subsection">
            <RequestForm defaults={own || {}} limits={view.limits} setFeedback={setFeedback} t={t}
              onDone={(next) => { setRequesting(false); onViewChange(next); }} />
          </div>
        )}
      </div>
    </Card>
  );
};

export default SenderSection;
